// T-171 (design D5, D10–D12): the one submitter of form definitions (<ds-form>, Shared/Form/_Form.cshtml). It replaces the page
// scripts that read each field by id and posted a hand-built FormData: it checks the required fields and the input rules, posts
// the named fields with the antiforgery token, and does what the definition says with each answer. It also keeps the declared
// conditions, asks a field's server reaction (OnChange → FormPatch) and offers page scripts the same field API (ds.form()).
import { $$, csrf } from './core.ts';
import { digitsOnly } from '../rules.ts';
import { flash } from './flash.ts';
import { toast, type ToastType } from './toast.ts';
import { validateForm } from './validation.ts';
import { FAILED, outcome, type FormAnswerRule } from './form-answer.ts';
import {
  cellOf,
  fileProblem,
  MESSAGES,
  fieldState,
  patchOf,
  rowsFrom,
  Sequencer,
  type FieldConfig,
  type PatchOp,
  type Row,
} from './form-logic.ts';
import { mountRows, type RowsHandle } from './form-rows.ts';
import type { EnhancedSelect } from './select.ts';

type Control = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

/** The field API of one form (design D12): by definition key, whatever id the control has. */
export interface FormApi {
  readonly element: HTMLFormElement;
  get: (key: string) => string | null;
  set: (key: string, value: string, text?: string | null) => void;
  options: (key: string, items: { value: string; label: string }[]) => void;
  show: (key: string) => void;
  hide: (key: string) => void;
  enable: (key: string) => void;
  disable: (key: string) => void;
  require: (key: string) => void;
  optional: (key: string) => void;
  error: (key: string, message: string) => void;
  clearErrors: () => void;
  apply: (patch: PatchOp[] | { patch?: unknown }) => void;
  /** A rows field's rows (design D14). */
  rows: (key: string) => Row[];
  /** Opens a step (design D15). */
  step: (index: number) => void;
  /** `change`, `change:<key>` (detail { key, value }), `before-submit` (cancelable), `answer` (detail: the outcome). */
  on: (event: string, handler: (e: CustomEvent) => void) => void;
}

/**
 * The values the form posts: every named control; a checkbox posts "true" or "false", as the old page scripts did; an amount
 * (rule money) its digits only — the separators are for the eye, and a decimal property would not bind «1,000,000».
 */
export function formData(form: HTMLFormElement): FormData {
  const data = new FormData();
  $$<Control>('input[name], select[name], textarea[name]', form).forEach(el => {
    if (el.disabled || !el.name) return;
    if (el instanceof HTMLInputElement && el.type === 'checkbox') data.append(el.name, el.checked ? 'true' : 'false');
    else if (el instanceof HTMLInputElement && el.dataset.rule === 'money') data.append(el.name, digitsOnly(el.value));
    else if (el instanceof HTMLInputElement && el.type === 'file')
      [...(el.files ?? [])].forEach(f => data.append(el.name, f));
    else if (el instanceof HTMLSelectElement && el.multiple)
      [...el.selectedOptions].forEach(o => data.append(el.name, o.value));
    else data.append(el.name, el.value);
  });
  return data;
}

const REQUIRED = MESSAGES.required;

/** The field's message line: the error in red, or back to its help. */
function message(field: HTMLElement | null, text: string | null): void {
  if (!field) return;
  // the field's own line first: a rows field holds the fields of its «new row», each with a line of its own
  const msg = field.querySelector<HTMLElement>(':scope > .ds-msg') ?? field.querySelector<HTMLElement>('.ds-msg');
  if (text === null) {
    field.classList.remove('is-invalid');
    if (msg?.dataset.info !== undefined) msg.innerHTML = msg.dataset.info;
    return;
  }
  field.classList.add('is-invalid');
  if (msg) {
    if (msg.dataset.info === undefined) msg.dataset.info = msg.innerHTML;
    msg.textContent = text;
  }
}

/**
 * The required controls left empty in root (aria-required set by the components); each gets the field's message line, and a
 * required field filled since the last try gets its line back. It runs after the rule check (validateForm) and leaves the other
 * fields alone: the rule check passes an empty field, so run the other way round it wiped the «fill this in» of a field with a
 * rule (an empty card or IBAN said nothing). The «new row» part of a rows field is checked by its add button, not by a submit.
 */
function missing(root: ParentNode): HTMLElement[] {
  const required = $$<Control>('[aria-required="true"]', root).filter(
    el => !el.disabled && !el.closest('.ds-rows-draft'),
  );
  const empty = required.filter(el => el.value.trim() === '');
  required.forEach(el => {
    const field = el.closest<HTMLElement>('.ds-field');
    if (empty.includes(el)) message(field, REQUIRED);
    else if (field?.querySelector(':scope > .ds-msg')?.textContent === REQUIRED) message(field, null);
  });
  return empty;
}

/** Design D16: a chosen file of a wrong type or too big; each gets the field's message line (FormValidator.CheckFile). */
function badFiles(root: ParentNode): HTMLElement[] {
  return $$<HTMLInputElement>('input[type=file]', root).filter(el => {
    const file = el.files?.[0];
    if (el.disabled || !file) return false;
    const problem = fileProblem(file.name, file.size, el.accept, el.dataset.maxMb ? Number(el.dataset.maxMb) : null);
    if (problem)
      message(el.closest<HTMLElement>('.ds-field'), problem === 'type' ? MESSAGES.fileType : MESSAGES.fileTooBig);
    return problem !== null;
  });
}

const apis = new WeakMap<HTMLFormElement, FormApi>();
const widgets = new Map<string, (frame: HTMLElement, form: FormApi) => void>();
// a value the field API sets fires 'change' for the dependants and the conditions, not the field's own server reaction
let quiet = false;

function create(form: HTMLFormElement): FormApi {
  const fields = JSON.parse(form.dataset.dsFields || '{}') as Record<string, FieldConfig>;
  const config = (key: string): FieldConfig | undefined => (Object.hasOwn(fields, key) ? fields[key] : undefined);
  const keyOfId = new Map(Object.entries(fields).map(([k, c]) => [c.id, k]));
  const control = (key: string): Control | null => {
    const id = config(key)?.id ?? key;
    return form.querySelector<Control>(`#${CSS.escape(id)}`);
  };
  const frame = (key: string): HTMLElement | null => control(key)?.closest<HTMLElement>('.ds-field') ?? null;
  const fire = (name: string, detail: unknown, cancelable = false): boolean =>
    form.dispatchEvent(new CustomEvent('ds-form:' + name, { detail, cancelable }));
  const redraw = (el: Control): void => (el as EnhancedSelect)._ds?.refresh();
  const setDisabled = (key: string, off: boolean): void => {
    const el = control(key);
    if (!el) return;
    el.disabled = off;
    redraw(el);
  };
  const setRequired = (key: string, on: boolean): void => {
    const el = control(key);
    if (!el) return;
    if (on) el.setAttribute('aria-required', 'true');
    else el.removeAttribute('aria-required');
    const label = frame(key)?.querySelector('.form-label');
    const star = label?.querySelector('.text-danger');
    if (on && label && !star)
      label.insertAdjacentHTML('beforeend', ' <span class="text-danger" aria-hidden="true">*</span>');
    if (!on) star?.remove();
  };

  const api: FormApi = {
    element: form,
    get(key) {
      const el = control(key);
      if (!el) return null;
      return el instanceof HTMLInputElement && el.type === 'checkbox' ? String(el.checked) : el.value;
    },
    set(key, value, text) {
      const el = control(key);
      if (!el) return;
      // design D16: a shown value (Display) is text; the server sends it formatted (text) or as the value
      if (!(el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement)) {
        (el as HTMLElement).textContent = text ?? value;
        return;
      }
      if (el instanceof HTMLInputElement && el.type === 'checkbox') el.checked = value === 'true';
      else if (el instanceof HTMLSelectElement) {
        if (value !== '' && ![...el.options].some(o => o.value === value)) el.add(new Option(text ?? value, value));
        el.value = value;
      } else el.value = value;
      redraw(el);
      quiet = true;
      try {
        // a text gets 'input' (the conditions read it) and 'change' (as a typed value would on blur)
        if (!(el instanceof HTMLSelectElement) && el.type !== 'checkbox')
          el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
      } finally {
        quiet = false;
      }
    },
    options(key, items) {
      const el = control(key);
      if (!(el instanceof HTMLSelectElement)) return;
      const current = el.value;
      [...el.options].forEach(o => {
        if (o.value !== '') o.remove();
      });
      items.forEach(i => el.add(new Option(i.label, i.value)));
      el.value = items.some(i => i.value === current) ? current : '';
      redraw(el);
    },
    show(key) {
      const f = frame(key);
      if (f) f.style.display = '';
      setDisabled(key, false);
    },
    hide(key) {
      const f = frame(key);
      if (f) f.style.display = 'none';
      setDisabled(key, true);
      message(f, null);
    },
    enable: key => setDisabled(key, false),
    disable(key) {
      setDisabled(key, true);
      message(frame(key), null);
    },
    require: key => setRequired(key, true),
    optional(key) {
      setRequired(key, false);
      message(frame(key), null);
    },
    error(key, text) {
      // a cell of a row (rows[1].amount): on the rows field, with the row's number
      const cell = cellOf(key);
      if (cell) {
        message(frame(cell.key), text === '' ? null : `ردیف ${cell.row + 1}: ${text}`);
        return;
      }
      message(frame(key), text === '' ? null : text);
    },
    rows: key => rowsByKey.get(key)?.get() ?? [],
    step: index => goTo(index),
    clearErrors() {
      $$('.ds-field.is-invalid', form).forEach(f => message(f, null));
    },
    apply(patch) {
      const ops = Array.isArray(patch) ? patch : patchOf(patch);
      for (const p of ops) {
        const key = p.field ?? '';
        switch (p.op) {
          case 'set':
            api.set(key, p.value ?? '', p.text);
            break;
          case 'options':
            api.options(key, p.items ?? []);
            break;
          case 'message':
            if (p.message) toast(p.message, { type: (p.kind ?? 'info') as ToastType });
            break;
          case 'error':
            api.error(key, p.message ?? '');
            break;
          case 'row-add':
            rowsFrom(p.rows).forEach(r => rowsByKey.get(key)?.add(r));
            break;
          case 'rows':
            rowsByKey.get(key)?.set(rowsFrom(p.rows));
            break;
          case 'step':
            goTo(Number(p.value));
            break;
          default:
            api[p.op](key);
        }
      }
    },
    on(event, handler) {
      const [name, key] = event.split(':');
      form.addEventListener('ds-form:' + name, e => {
        const ce = e as CustomEvent<{ key?: string }>;
        if (!key || ce.detail.key === key) handler(ce);
      });
    },
  };

  // conditions (design D10): on start and after every change, through the same API
  const conditions = (): void => {
    for (const [key, config] of Object.entries(fields)) {
      const state = fieldState(config, api.get);
      if (state.visible !== null) (state.visible ? api.show : api.hide)(key);
      if (state.enabled !== null && state.visible !== false) (state.enabled ? api.enable : api.disable)(key);
      if (state.required !== null) (state.required ? api.require : api.optional)(key);
    }
  };

  // server reactions (design D11): newest answer per field wins; a submit waits for the pending ones
  const order = new Sequencer();
  const pending = new Set<Promise<void>>();
  const timers = new Map<string, { id: number; run: () => void }>();
  const react = (key: string): void => {
    const url = config(key)?.change?.url;
    if (!url) return;
    const n = order.next(key);
    const body = formData(form);
    body.append('_changed', key);
    const job = fetch(url, {
      method: 'POST',
      body,
      credentials: 'same-origin',
      headers: { RequestVerificationToken: csrf(), Accept: 'application/json' },
    })
      .then(async res => {
        if (!res.ok) throw new Error(String(res.status));
        const answer: unknown = await res.json();
        if (order.isLatest(key, n)) api.apply(patchOf(answer));
      })
      .catch(() => {
        if (order.isLatest(key, n)) toast(FAILED, { type: 'danger' });
      })
      .finally(() => pending.delete(job));
    pending.add(job);
  };
  const schedule = (key: string, wait: number): void => {
    const old = timers.get(key);
    if (old) clearTimeout(old.id);
    if (wait <= 0) {
      timers.delete(key);
      react(key);
      return;
    }
    const run = (): void => {
      timers.delete(key);
      react(key);
    };
    timers.set(key, { id: window.setTimeout(run, wait), run });
  };
  const changed = (e: Event): void => {
    const el = e.target as Control;
    const key = keyOfId.get(el.id);
    if (!key) return;
    const choice = el instanceof HTMLSelectElement || (el instanceof HTMLInputElement && el.type === 'checkbox');
    // a select or a check reacts on 'change'; a text on 'input' (debounced) — not again on the 'change' of its blur
    if ((e.type === 'change') !== choice) return;
    conditions();
    fire('change', { key, value: api.get(key) });
    const reaction = config(key)?.change;
    if (reaction && !quiet) schedule(key, choice ? 0 : reaction.debounce);
  };
  form.addEventListener('change', changed);
  form.addEventListener('input', changed);

  /**
   * Posts the form with extra values to an action that answers a patch (a checked new row, a step's check) and applies it;
   * true when the answer refused nothing (no error with a message, not `ok: false`).
   */
  const ask = async (url: string, extra: [string, string][]): Promise<boolean> => {
    const body = formData(form);
    extra.forEach(([k, v]) => body.append(k, v));
    try {
      const res = await fetch(url, {
        method: 'POST',
        body,
        credentials: 'same-origin',
        headers: { RequestVerificationToken: csrf(), Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(String(res.status));
      const answer: unknown = await res.json();
      const ops = patchOf(answer);
      api.apply(ops);
      const refused = (answer as { ok?: unknown } | null)?.ok === false;
      if (refused) {
        const text = (answer as { message?: unknown }).message;
        if (typeof text === 'string' && text) toast(text, { type: 'danger' });
      }
      return !refused && !ops.some(o => o.op === 'error' && o.message);
    } catch {
      toast(FAILED, { type: 'danger' });
      return false;
    }
  };

  // rows (design D14)
  const rowsByKey = new Map<string, RowsHandle>();
  for (const [key, c] of Object.entries(fields)) {
    const rowsFrame = c.rows ? form.querySelector<HTMLElement>(`[data-ds-rows="${CSS.escape(key)}"]`) : null;
    if (!c.rows || !rowsFrame) continue;
    rowsByKey.set(
      key,
      mountRows(rowsFrame, key, c.rows, key, {
        error: (k, text) => api.error(k, text),
        ask,
        changed: k => {
          conditions();
          fire('change', { key: k, value: api.get(k) });
        },
      }),
    );
  }

  /** Too few or too many rows in root: the message on each such rows field. */
  const rowsProblems = (root: ParentNode): HTMLElement[] => {
    const bad: HTMLElement[] = [];
    rowsByKey.forEach(handle => {
      const el = control(handle.key);
      if (!el || el.disabled || (root !== form && !(root as Element).contains(el))) return;
      const problem = handle.problem();
      if (problem) {
        api.error(handle.key, problem);
        bad.push(el);
      }
    });
    return bad;
  };

  // steps (design D15): one step at a time; «next» checks the step (and asks its action), «back» never checks
  const steps = $$('[data-ds-step]', form);
  const tabs = $$('[data-ds-step-tab]', form);
  const back = form.querySelector<HTMLButtonElement>('[data-ds-step-back]');
  const next = form.querySelector<HTMLButtonElement>('[data-ds-step-go]');
  const save = form.querySelector<HTMLButtonElement>('button[type=submit]');
  let current = 0;
  function goTo(index: number): void {
    if (!steps.length || !(index >= 0 && index < steps.length)) return;
    current = index;
    steps.forEach((s, i) => (s.hidden = i !== index));
    tabs.forEach((t, i) => {
      t.classList.toggle('is-current', i === index);
      t.classList.toggle('is-done', i < index);
      t.setAttribute('aria-current', i === index ? 'step' : 'false');
    });
    if (back) back.hidden = index === 0;
    if (next) next.hidden = index === steps.length - 1;
    if (save) save.hidden = index !== steps.length - 1;
    steps[index].querySelector<HTMLElement>('input:not([type=hidden]), select, textarea, .ds-select-btn')?.focus();
    fire('step', { index });
  }
  const checkStep = (root: HTMLElement): boolean => {
    const valid = validateForm(form, root);
    const empty = [...missing(root), ...badFiles(root)];
    const rowsBad = rowsProblems(root);
    if (!valid || empty.length || rowsBad.length) {
      [...empty, ...rowsBad]
        .at(0)
        ?.closest('.ds-field')
        ?.querySelector<HTMLElement>('.ds-select-btn, input, textarea')
        ?.focus();
      return false;
    }
    return true;
  };
  back?.addEventListener('click', () => goTo(current - 1));
  next?.addEventListener('click', () => {
    void (async () => {
      const section = steps[current];
      if (!checkStep(section)) return;
      const url = section.dataset.dsStepNext;
      if (url) {
        next.disabled = true;
        const at = current;
        const ok = await ask(url, [['_step', String(at)]]).finally(() => (next.disabled = false));
        // the action may open another step itself (a patch «step»); otherwise the next one opens
        if (!ok || current !== at) return;
      }
      goTo(current + 1);
    })();
  });
  if (steps.length) goTo(0);

  async function submit(button: HTMLButtonElement | null): Promise<void> {
    timers.forEach(t => {
      clearTimeout(t.id);
      t.run();
    });
    if (pending.size) await Promise.allSettled([...pending]);
    if (!fire('before-submit', {}, true)) return;
    const valid = validateForm(form);
    const empty = [...missing(form), ...badFiles(form)];
    const rowsBad = rowsProblems(form);
    if (!valid || empty.length || rowsBad.length) {
      const first = [...empty, ...rowsBad].at(0) ?? form.querySelector<HTMLElement>('.ds-field.is-invalid input');
      // a field of another step: open that step first
      const owner = first?.closest<HTMLElement>('[data-ds-step]');
      if (owner) goTo(Number(owner.dataset.dsStep));
      first?.closest('.ds-field')?.querySelector<HTMLElement>('.ds-select-btn, input, textarea')?.focus();
      return;
    }
    const rules = JSON.parse(form.dataset.dsAnswers || '[]') as FormAnswerRule[];
    if (button) button.disabled = true;
    try {
      const res = await fetch(form.action, {
        method: 'POST',
        body: formData(form),
        credentials: 'same-origin',
        headers: { RequestVerificationToken: csrf(), Accept: 'application/json' },
      });
      const type = res.headers.get('content-type') || '';
      const body: unknown = type.includes('json') ? await res.json() : null;
      api.apply(patchOf(body));
      const result = outcome(rules, { status: res.status, redirected: res.redirected, url: res.url, body });
      fire('answer', result);
      if (result.kind === 'go') {
        if (result.message) flash(result.message, { type: result.type });
        location.href = result.url;
        return;
      }
      if (result.message) toast(result.message, { type: result.type });
    } catch {
      toast(FAILED, { type: 'danger' });
    } finally {
      if (button) button.disabled = false;
    }
  }

  form.addEventListener('submit', e => {
    e.preventDefault();
    void submit(form.querySelector<HTMLButtonElement>('button[type=submit]'));
  });
  apis.set(form, api);
  conditions();
  $$('[data-ds-widget]', form).forEach(el => startWidget(el, api));
  return api;
}

function startWidget(frame: HTMLElement, api: FormApi): void {
  const init = widgets.get(frame.dataset.dsWidget ?? '');
  if (!init || frame.dataset.dsWidgetReady) return;
  frame.dataset.dsWidgetReady = '1';
  init(frame, api);
}

/** The field API of a definition form (`ds.form('form-receipt')` or the element); null when it is not one. */
export function form(target: string | HTMLFormElement): FormApi | null {
  const el = typeof target === 'string' ? document.getElementById(target) : target;
  if (!(el instanceof HTMLFormElement) || el.dataset.dsForm === undefined) return null;
  return apis.get(el) ?? create(el);
}

/** `ds.forms.widget(name, init)`: a custom field's browser side, started once per field frame (design D9). */
export const forms = {
  widget(name: string, init: (frame: HTMLElement, form: FormApi) => void): void {
    widgets.set(name, init);
    $$<HTMLFormElement>('form[data-ds-form]').forEach(f => {
      const api = apis.get(f);
      if (api) $$(`[data-ds-widget="${CSS.escape(name)}"]`, f).forEach(el => startWidget(el, api));
    });
  },
};

/** Wires every definition form on the page. */
export function initForms(): void {
  $$<HTMLFormElement>('form[data-ds-form]').forEach(f => form(f));
}
